import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  ShoppingBag,
  Plus,
  Minus,
  Trash2,
  ArrowRight,
  MessageCircle,
  Sparkles,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useApp } from '@/context/AppContext';

export default function CartPage() {
  const { cart, removeFromCart, updateCartQuantity, cartTotal, clearCart } = useApp();

  const formatPrice = (price) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0,
    }).format(price);
  };

  const handleWhatsAppCheckout = () => {
    const cartItems = cart
      .map(
        (item) =>
          `- ${item.name} x${item.quantity} = ${formatPrice(item.price * item.quantity)}${
            item.customization ? ' (Customized)' : ''
          }`
      )
      .join('\n');

    const message = encodeURIComponent(
      `Assalamu Alaikum! I would like to place an order:\n\n${cartItems}\n\nTotal: ${formatPrice(cartTotal)}\n\nPlease confirm my order.`
    );

    window.open(`https://wa.me/919111841033?text=${message}`, '_blank');
  };

  return (
    <div className="page-transition min-h-screen">
      {/* Header */}
      <div className="bg-gradient-burgundy-soft border-b border-border/30">
        <div className="container mx-auto px-4 py-12 md:py-16">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center"
          >
            <div className="inline-flex items-center gap-2 mb-4">
              <ShoppingBag className="h-6 w-6 text-silver" />
            </div>
            <h1 className="font-display text-3xl md:text-4xl text-foreground mb-2">
              Your Cart
            </h1>
            <p className="text-muted-foreground font-body">
              {cart.length} {cart.length === 1 ? 'item' : 'items'} in your cart
            </p>
          </motion.div>
        </div>
      </div>

      <div className="container mx-auto px-4 py-8 md:py-12">
        {cart.length === 0 ? (
          // Empty Cart
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center py-16 max-w-md mx-auto"
          >
            <div className="h-20 w-20 rounded-full bg-muted/50 flex items-center justify-center mx-auto mb-6">
              <ShoppingBag className="h-10 w-10 text-muted-foreground" />
            </div>
            <h2 className="font-display text-xl text-silver-light mb-3">
              Your cart is empty
            </h2>
            <p className="text-muted-foreground font-body mb-6">
              Start adding beautiful creations to your cart
            </p>
            <Link to="/products">
              <Button variant="ivory" size="lg">
                Browse Products
                <ArrowRight className="h-4 w-4" />
              </Button>
            </Link>
          </motion.div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Cart Items */}
            <div className="lg:col-span-2 space-y-4">
              {cart.map((item, index) => (
                <motion.div
                  key={`${item.id}-${index}`}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.05 }}
                  className="flex gap-4 md:gap-6 p-4 md:p-6 bg-card rounded-xl border border-border/50"
                >
                  {/* Image */}
                  <Link to={`/product/${item.id}`}>
                    <div className="h-24 w-24 md:h-32 md:w-32 rounded-lg overflow-hidden bg-muted flex-shrink-0">
                      <img
                        src={item.images?.[0]}
                        alt={item.name}
                        className="h-full w-full object-cover hover:scale-105 transition-transform duration-300"
                      />
                    </div>
                  </Link>

                  {/* Info */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-4">
                      <div>
                        <Link to={`/product/${item.id}`}>
                          <h3 className="font-display text-base md:text-lg text-silver-light hover:text-ivory transition-colors">
                            {item.name}
                          </h3>
                        </Link>
                        {item.customization && (
                          <div className="flex items-center gap-2 mt-1">
                            <Sparkles className="h-3 w-3 text-silver" />
                            <span className="text-xs text-muted-foreground">
                              Customized Order
                            </span>
                          </div>
                        )}
                      </div>
                      <button
                        onClick={() => removeFromCart(item.id, item.customization)}
                        className="text-muted-foreground hover:text-destructive transition-colors p-1 flex-shrink-0"
                        aria-label="Remove item"
                      >
                        <Trash2 className="h-5 w-5" />
                      </button>
                    </div>

                    {/* Price and Quantity */}
                    <div className="flex flex-col md:flex-row md:items-center md:justify-between mt-4 gap-4">
                      {/* Quantity */}
                      <div className="flex items-center gap-3">
                        <span className="text-sm text-muted-foreground">Qty:</span>
                        <button
                          onClick={() =>
                            updateCartQuantity(
                              item.id,
                              item.quantity - 1,
                              item.customization
                            )
                          }
                          className="h-8 w-8 rounded-lg border border-border flex items-center justify-center text-muted-foreground hover:text-silver hover:border-silver/50 transition-colors"
                        >
                          <Minus className="h-4 w-4" />
                        </button>
                        <span className="w-8 text-center font-medium text-foreground">
                          {item.quantity}
                        </span>
                        <button
                          onClick={() =>
                            updateCartQuantity(
                              item.id,
                              item.quantity + 1,
                              item.customization
                            )
                          }
                          className="h-8 w-8 rounded-lg border border-border flex items-center justify-center text-muted-foreground hover:text-silver hover:border-silver/50 transition-colors"
                        >
                          <Plus className="h-4 w-4" />
                        </button>
                      </div>

                      {/* Price */}
                      <div className="text-right">
                        <span className="font-display text-lg text-ivory">
                          {formatPrice(item.price * item.quantity)}
                        </span>
                        {item.quantity > 1 && (
                          <p className="text-xs text-muted-foreground">
                            {formatPrice(item.price)} each
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                </motion.div>
              ))}

              {/* Clear Cart */}
              <div className="text-right pt-2">
                <button
                  onClick={clearCart}
                  className="text-sm text-muted-foreground hover:text-destructive transition-colors"
                >
                  Clear Cart
                </button>
              </div>
            </div>

            {/* Order Summary */}
            <div className="lg:col-span-1">
              <div className="sticky top-24 bg-card rounded-xl border border-border/50 p-6">
                <h2 className="font-display text-xl text-silver-light mb-6">
                  Order Summary
                </h2>

                <div className="space-y-4 mb-6">
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Subtotal</span>
                    <span className="text-foreground">{formatPrice(cartTotal)}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Shipping</span>
                    <span className="text-muted-foreground">Calculated at checkout</span>
                  </div>
                  <div className="border-t border-border pt-4 flex justify-between">
                    <span className="font-display text-silver-light">Total</span>
                    <span className="font-display text-xl text-ivory">
                      {formatPrice(cartTotal)}
                    </span>
                  </div>
                </div>

                {/* Checkout Buttons */}
                <div className="space-y-3">
                  <Button
                    variant="whatsapp"
                    size="lg"
                    className="w-full"
                    onClick={handleWhatsAppCheckout}
                  >
                    <MessageCircle className="h-5 w-5" />
                    Checkout via WhatsApp
                  </Button>
                </div>

                {/* Trust Note */}
                <p className="text-xs text-muted-foreground text-center mt-6 font-body">
                  Secure ordering via WhatsApp. Payment options available.
                </p>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
